'use client';

import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { IconButton } from '../../components/icon-button';
import { Clipboard } from '../../components/clipboard';
import { Tick01Icon, Copy01Icon, Refresh04Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState, useEffect, useRef } from 'react';

interface OtpDisplayProps {
  code: string;
  isLoading?: boolean;
  onRefresh?: () => void;
  disabled?: boolean;
}

export function OtpDisplay({
  code,
  isLoading = false,
  onRefresh,
  disabled = false,
}: OtpDisplayProps) {
  const t = useTranslations('designSystem.mfa');
  const STEP_SECONDS = 30;
  const [timeLeft, setTimeLeft] = useState<number>(STEP_SECONDS);
  const lastWindowRef = useRef<number>(
    Math.floor(Date.now() / 1000 / STEP_SECONDS),
  );
  const computeSecondsUntilNextStep = (): number => {
    const nowSec = Math.floor(Date.now() / 1000);
    const remainder = nowSec % STEP_SECONDS;

    return remainder === 0 ? STEP_SECONDS : STEP_SECONDS - remainder;
  };

  useEffect(() => {
    if (disabled) {
      return;
    }

    // Initialize aligned countdown immediately
    setTimeLeft(computeSecondsUntilNextStep());
    const timer = setInterval(() => {
      const nowSec = Math.floor(Date.now() / 1000);
      const currentWindow = Math.floor(nowSec / STEP_SECONDS);
      const secondsLeft = computeSecondsUntilNextStep();

      // Trigger refresh once per new time window
      if (currentWindow !== lastWindowRef.current) {
        lastWindowRef.current = currentWindow;
        onRefresh?.();
      }

      setTimeLeft(secondsLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, [onRefresh, disabled]);

  // Simple text countdown - much cleaner than circular timer
  return (
    <VStack alignItems="stretch" gap={4}>
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" fontWeight="medium" color="fg.default">
          {t('currentOtpCode')}
        </Text>
        <HStack gap={3} alignItems="center">
          <Text fontSize="sm" color="fg.muted">
            {disabled ? t('disabled') : t('refreshesIn', { seconds: timeLeft })}
          </Text>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading || disabled}
            aria-label={t('refreshCode')}
          >
            <Refresh04Icon
              size={14}
              style={{
                animation: isLoading ? 'spin 1s linear infinite' : 'none',
              }}
            />
          </IconButton>
        </HStack>
      </HStack>

      <Box
        p={4}
        borderRadius="lg"
        bg="bg.muted"
        borderWidth="1px"
        borderColor="border.muted"
      >
        <HStack justifyContent="space-between" alignItems="center">
          <Text
            fontSize="2xl"
            fontWeight="bold"
            fontFamily="mono"
            letterSpacing="0.15em"
            color={
              disabled ? 'fg.disabled' : isLoading ? 'fg.muted' : 'fg.default'
            }
          >
            {disabled ? '••••••' : isLoading ? '••••••' : code}
          </Text>

          <Clipboard.Root value={code}>
            <Clipboard.Trigger asChild>
              <IconButton
                variant="outline"
                size="sm"
                disabled={isLoading || !code || disabled}
              >
                <Clipboard.Indicator copied={<Tick01Icon size={16} />}>
                  <Copy01Icon size={16} />
                </Clipboard.Indicator>
              </IconButton>
            </Clipboard.Trigger>
          </Clipboard.Root>
        </HStack>
      </Box>

      <Text fontSize="xs" color="fg.muted" textAlign="center">
        {t('otpCodesGenerated')}
      </Text>
    </VStack>
  );
}

export type { OtpDisplayProps };
